/**
 * Application shell.
 *
 * One navigation, at every width: five tabs along the bottom, in the order the
 * design puts them — Bridge, Projects, Crew, Journeys, Control. The design
 * labels the second one "Missions", and it is the only one of the five that is
 * not already the product's own word: the screen is titled Projects, the API
 * says projects, the ADRs say projects. A tab that renames the thing on its way
 * to it is a costume, not a label. There used to be
 * two, a nine-item rail on desktop and a five-item bar on phones, which meant
 * two different products depending on the width of the window and two different
 * answers to "where is that screen?".
 *
 * Five is not enough slots for nine destinations, and the design is right about
 * that rather than incomplete: what is missing from the bar is what an operator
 * does not steer by. Attention lives on the Bridge, where it is looked at; the
 * rest — agents, forecast, integrations, accounts, audit — is administration and
 * lives behind Control, which carries the count so nothing waiting can be
 * invisible.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export interface ShellCounts {
  interventions: number;
  critical: number;
}

/**
 * The bar. `end` on the Bridge so it is only current at exactly `/`, and the
 * count rides on Control because that is where what it counts can be reached.
 */
const TABS = [
  { to: '/', label: 'Bridge', glyph: '◎', end: true },
  { to: '/projects', label: 'Projects', glyph: '▤' },
  { to: '/crew', label: 'Crew', glyph: '◍' },
  { to: '/journeys', label: 'Journeys', glyph: '◈' },
  { to: '/control', label: 'Control', glyph: '⚙', badge: true },
];

export function Shell({ children, counts }: { children: ReactNode; counts: ShellCounts }) {
  const { user } = useAuth();

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      {/* The brand and the person, on one line, the way the design has them —
          and the only place either appears now that the rail is gone. */}
      <header className="deckhead">
        <div className="deckhead__brand">
          <span className="deckhead__mark" aria-hidden="true">
            ◭
          </span>
          <span className="deckhead__name">
            NEWZ BRIDGE
            <span className="deckhead__sub">AI development command center</span>
          </span>
        </div>
        {user && (
          <NavLink to="/control" className="deckhead__who">
            <span className="deckhead__avatar" aria-hidden="true">
              {user.displayName.slice(0, 1)}
            </span>
            <span>
              {user.displayName}
              <span className="deckhead__role">
                {user.tenantSlug} · {user.role.replace('_', ' ')}
              </span>
            </span>
          </NavLink>
        )}
      </header>

      <main className="main" id="main">
        {children}
      </main>

      <nav className="tabbar" aria-label="Primary">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className="tabbar__link">
            <span className="tabbar__glyph" aria-hidden="true">
              {tab.glyph}
            </span>
            <span className="tabbar__label">{tab.label}</span>
            {tab.badge && counts.interventions > 0 && (
              <span
                className="tabbar__badge"
                data-tone={counts.critical > 0 ? 'critical' : 'risk'}
                aria-label={`${counts.interventions} open`}
              >
                {counts.interventions}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
